import React, { useState, useEffect } from "react";
import Navbar from "../../layout/Navbar";
import axios from "axios";

const BedAvailability = () => {
    // 0: Screen1, 1: Screen3, 2: Screen4
    const [screen, setScreen] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setScreen(prevScreen => {
                if (prevScreen === 0) return 1; // Screen1 → Screen3
                if (prevScreen === 1) return 2; // Screen3 → Screen4
                return 0; // Screen4 → Screen1
            });
        }, 30000);
        
        return () => clearTimeout(timer);
    }, [screen]);

    // Screen 1: Dashboard (existing)
    const Screen1 = () => {
        const [apiData, setApiData] = useState(null);
        const [triageData, setTriageData] = useState(null);
        const [loading, setLoading] = useState(true);
        const [lastUpdated, setLastUpdated] = useState('');

        useEffect(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);

                    // Fetch bed status data
                    const bedResponse = await axios.get('https://traumacentrebhu.com/bhulive/dashboard/footfall/bed?type=STATUS');
                    const bedResult = bedResponse.data;

                    // Fetch triage data
                    const triageResponse = await axios.get('https://traumacentrebhu.com/bhulive/dashboard/footfall/triage');
                    const triageResult = triageResponse.data;

                    if (bedResult.status === 'success') {
                        setApiData(bedResult.data);
                    }

                    setTriageData(triageResult);

                    // Set current time when API is hit
                    const now = new Date();
                    const day = String(now.getDate()).padStart(2, '0');
                    const month = now.toLocaleDateString('en-US', { month: 'short' });
                    const year = now.getFullYear();
                    const time = now.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                    setLastUpdated(`${day}-${month}-${year} ${time}`);

                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }, []);

        // Transform API data to ward array format and filter out null departments
        const transformedWards = apiData ? Object.entries(apiData)
            .filter(([wardName, data]) => wardName !== 'null' && wardName !== null && wardName !== '')
            .map(([wardName, data]) => {
                // Handle both array and object data structures
                const wardData = Array.isArray(data) ? (data[0] || {}) : (data || {});
                return {
                    name: wardName,
                    available: wardData.Available || 0,
                    occupied: wardData.Occupied || 0,
                    blocked: wardData.Blocked || 0,
                    unavailable: wardData.Unavailable || 0,
                    markedForDischarge: wardData['Marked For Discharge'] || 0
                };
            }) : [];

        const totalAvailable = transformedWards.reduce((sum, ward) => sum + ward.available, 0);
        const totalOccupied = transformedWards.reduce((sum, ward) => sum + ward.occupied, 0);

        const getCurrentDateTime = () => {
            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = now.toLocaleDateString('en-US', { month: 'short' });
            const year = now.getFullYear();
            const time = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return `${day}-${month}-${year} ${time}`;
        };

        return (
            <>
                <Navbar />
                <div className="min-h-screen bg-gradient-to-br from-orange-400 to-orange-500 p-6">

                    {/* Dashboard Info Box */}
                    <div className="bg-yellow-100 border-2 border-orange-300 rounded-lg p-6 mb-8 text-center">
                        <h2 className="text-brown-800 text-4xl font-bold mb-2">Dashboard / डैशबोर्ड</h2>
                        <p className="text-brown-700 text-xl mb-1">The Dashboard Data will be refresh every 5 minutes / डैशबोर्ड डेटा हर 5 मिनट में रिफ्रेश हो जाएगा</p>
                        <p className="text-brown-700 text-xl">Data as on Date & Time : {lastUpdated || getCurrentDateTime()} / दिनांक और समय के अनुसार डेटा :</p>
                    </div>

                    {/* Bed Statistics */}
                    <div className="max-w-4xl mx-auto space-y-6">
                        {/* Total Beds */}
                        <div className="bg-blue-200 rounded-lg p-6 text-center shadow-lg">
                            <h3 className="text-brown-800 text-4xl font-bold mb-4">Total Number of Beds / बेडों की कुल संख्या</h3>
                            <p className="text-brown-800 text-4xl font-bold">{354}</p>
                        </div>

                        {/* Ward Table */}
                        <div className="bg-white rounded-lg p-6 shadow-lg">
                            {loading ? (
                                <div className="text-center text-gray-600 text-xl">Loading ward data...</div>
                            ) : (
                                <table className="border border-black text-center w-full text-lg shadow-lg">
                                    <thead>
                                        <tr>
                                            <th className="bg-gray-300 border border-black px-4 py-2 font-bold">Ward / वार्ड</th>
                                            <th className="bg-green-200 border border-black px-4 py-2 font-bold">Beds Available / उपलब्ध बेड</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transformedWards.map((ward, idx) => (
                                            <tr key={ward.name} className={idx % 2 === 0 ? "bg-yellow-100" : "bg-yellow-200"}>
                                                <td className="border border-black px-4 py-2 font-bold">{ward.name}</td>
                                                <td className="border border-black px-4 py-2 font-semibold">{ward.available}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Emergency Waiting - Same design as Total Beds */}
                        <div className="bg-purple-200 rounded-lg p-6 text-center shadow-lg">
                            <h3 className="text-brown-800 text-4xl font-bold mb-4">Patient in Emergency Waiting for Admission / आपातकाल में भर्ती के लिए प्रतीक्षारत मरीज़</h3>
                            <p className="text-brown-800 text-4xl font-bold">
                                {triageData && triageData["RED AREA OBSERVATION"] ? triageData["RED AREA OBSERVATION"] : 'Loading...'}
                            </p>
                        </div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="fixed bottom-0 right-0 opacity-20">
                        <div className="w-32 h-32 border-8 border-white transform rotate-45 translate-x-16 translate-y-16"></div>
                        <div className="w-24 h-24 border-6 border-white transform rotate-45 translate-x-20 translate-y-8"></div>
                        <div className="w-16 h-16 border-4 border-white transform rotate-45 translate-x-24 translate-y-4"></div>
                    </div>
                </div>
            </>
        );
    };

    // Screen 3: Comprehensive ward table with API integration
    const Screen3 = () => {
        const [apiData, setApiData] = useState(null);
        const [triageData, setTriageData] = useState(null);
        const [loading, setLoading] = useState(true);
        const [lastUpdated, setLastUpdated] = useState('');

        useEffect(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);

                    // Fetch doctor data
                    const doctorResponse = await axios.get('https://traumacentrebhu.com/bhulive/dashboard/footfall/bed?type=DOCTOR');
                    const doctorResult = doctorResponse.data;

                    // Fetch triage data
                    const triageResponse = await axios.get('https://traumacentrebhu.com/bhulive/dashboard/footfall/triage');
                    const triageResult = triageResponse.data;

                    if (doctorResult.status === 'success') {
                        setApiData(doctorResult.data);
                    }

                    setTriageData(triageResult);

                    // Set current time when API is hit
                    const now = new Date();
                    const day = String(now.getDate()).padStart(2, '0');
                    const month = now.toLocaleDateString('en-US', { month: 'short' });
                    const year = now.getFullYear();
                    const time = now.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                    setLastUpdated(`${day}-${month}-${year} ${time}`);

                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }, []);

        // Transform API data to doctor array format and filter out null doctors
        const transformedDoctors = apiData ? Object.entries(apiData)
            .filter(([doctorName, data]) => doctorName !== 'null' && doctorName !== null && doctorName !== '')
            .map(([doctorName, data]) => {
                // Handle both array and object data structures
                const doctorData = Array.isArray(data) ? (data[0] || {}) : (data || {});
                return {
                    name: doctorName,
                    available: doctorData.Available || 0,
                    occupied: doctorData.Occupied || 0,
                    blocked: doctorData.Blocked || 0,
                    unavailable: doctorData.Unavailable || 0,
                    markedForDischarge: doctorData['Marked For Discharge'] || 0
                };
            })
            .sort((a, b) => {
                // First priority: Exact match "PROF. SAURABH SINGH"
                if (a.name === "PROF. SAURABH SINGH") return -1;
                if (b.name === "PROF. SAURABH SINGH") return 1;

                // Second priority: Contains "PROF. SAURABH SINGH" (joint entries)
                const aContainsProfSaurabh = a.name.includes("PROF. SAURABH SINGH") && a.name !== "PROF. SAURABH SINGH";
                const bContainsProfSaurabh = b.name.includes("PROF. SAURABH SINGH") && b.name !== "PROF. SAURABH SINGH";

                if (aContainsProfSaurabh && !bContainsProfSaurabh) return -1;
                if (!aContainsProfSaurabh && bContainsProfSaurabh) return 1;

                // Third priority: TEAM A, TEAM B, etc.
                const teamRegex = /^TEAM\s[A-Z]/i;
                const aIsTeam = teamRegex.test(a.name);
                const bIsTeam = teamRegex.test(b.name);

                if (aIsTeam && !bIsTeam) return -1; // TEAM comes first
                if (!aIsTeam && bIsTeam) return 1;  // TEAM comes first

                // If both are teams or both are not teams, sort alphabetically
                return a.name.localeCompare(b.name);
            }) : [];

        const totalPatients = transformedDoctors.reduce((sum, doctor) => sum + doctor.available, 0);

        return (
            <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-orange-400 to-orange-500 p-6">

                {/* Dashboard Info Box */}
                <div className="bg-yellow-100 border-2 border-orange-300 rounded-lg p-6 mb-8 text-center">
                    <h2 className="text-brown-800 text-4xl font-bold mb-2">Dashboard / डैशबोर्ड</h2>
                    <p className="text-brown-700 text-xl mb-1">The Dashboard Data will be refresh every 5 minutes / डैशबोर्ड डेटा हर 5 मिनट में रिफ्रेश हो जाएगा</p>
                    <p className="text-brown-700 text-xl">Data as on Date & Time : {lastUpdated || 'Loading...'} / दिनांक और समय के अनुसार डेटा :</p>
                </div>

                {/* Doctor Statistics */}
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Total Number of Beds */}
                    <div className="bg-green-200 rounded-lg p-6 text-center shadow-lg">
                        <h3 className="text-brown-800 text-4xl font-bold mb-4">Total Number of Beds / बेडों की कुल संख्या</h3>
                        <p className="text-brown-800 text-4xl font-bold">{354}</p>
                    </div>

                    {/* Doctor Table */}
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        {loading ? (
                            <div className="text-center text-gray-600 text-xl">Loading doctor data...</div>
                        ) : (
                            <table className="border border-black text-center w-full text-lg shadow-lg">
                                <thead>
                                    <tr>
                                        <th className="bg-gray-300 border border-black px-4 py-2 font-bold">Doctor / डॉक्टर</th>
                                        <th className="bg-green-200 border border-black px-4 py-2 font-bold">Available Beds / उपलब्ध बेड</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transformedDoctors.map((doctor, idx) => (
                                        <tr key={doctor.name} className={idx % 2 === 0 ? "bg-yellow-100" : "bg-yellow-200"}>
                                            <td className="border border-black px-4 py-2 font-bold">{doctor.name}</td>
                                            <td className="border border-black px-4 py-2 font-semibold">{doctor.available}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Emergency Waiting - Same design as Total Patients */}
                    <div className="bg-purple-200 rounded-lg p-6 text-center shadow-lg">
                        <h3 className="text-brown-800 text-4xl font-bold mb-4">Patient in Emergency Waiting for Admission / आपातकाल में भर्ती के लिए प्रतीक्षारत मरीज़</h3>
                        <p className="text-brown-800 text-4xl font-bold">
                            {triageData && triageData["RED AREA OBSERVATION"] ? triageData["RED AREA OBSERVATION"] : 'Loading...'}
                        </p>
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="fixed bottom-0 right-0 opacity-20">
                    <div className="w-32 h-32 border-8 border-white transform rotate-45 translate-x-16 translate-y-16"></div>
                    <div className="w-24 h-24 border-6 border-white transform rotate-45 translate-x-20 translate-y-8"></div>
                    <div className="w-16 h-16 border-4 border-white transform rotate-45 translate-x-24 translate-y-4"></div>
                </div>
            </div>
            </>
        );
    };

    const Screen4 = () => {
        const [apiData, setApiData] = useState(null);
        const [loading, setLoading] = useState(true);
        const [lastUpdated, setLastUpdated] = useState('');

        useEffect(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    
                    // Fetch doctor last admission data
                    const doctorResponse = await axios.get('https://traumacentrebhu.com/bhulive/dashboard/footfall/lastadm');
                    const doctorResult = doctorResponse.data;
                    
                    if (doctorResult.status === 'success') {
                        setApiData(doctorResult.data);
                    }
                    
                    // Set current time when API is hit
                    const now = new Date();
                    const day = String(now.getDate()).padStart(2, '0');
                    const month = now.toLocaleDateString('en-US', { month: 'short' });
                    const year = now.getFullYear();
                    const time = now.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit', 
                        hour12: false 
                    });
                    setLastUpdated(`${day}-${month}-${year} ${time}`);
                    
                } catch (error) {
                    console.error('Error fetching doctor admission data:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }, []);

        // Sort doctors with same logic as Screen3
        const sortedDoctors = apiData ? [...apiData].sort((a, b) => {
            // First priority: Exact match variations of SAURABH SINGH
            const aSaurabh = a.doctorName === "PROF. SAURABH SINGH" || a.doctorName === "Prof. SAURABH SINGH" || a.doctorName.includes("SAURABH SINGH");
            const bSaurabh = b.doctorName === "PROF. SAURABH SINGH" || b.doctorName === "Prof. SAURABH SINGH" || b.doctorName.includes("SAURABH SINGH");
            
            if (aSaurabh && !bSaurabh) return -1;
            if (!aSaurabh && bSaurabh) return 1;

            // If both contain SAURABH SINGH, prioritize exact matches
            if (aSaurabh && bSaurabh) {
                const aExact = a.doctorName === "PROF. SAURABH SINGH" || a.doctorName === "Prof. SAURABH SINGH";
                const bExact = b.doctorName === "PROF. SAURABH SINGH" || b.doctorName === "Prof. SAURABH SINGH";
                
                if (aExact && !bExact) return -1;
                if (!aExact && bExact) return 1;
            }

            // Third priority: TEAM A, TEAM B, etc.
            const teamRegex = /^TEAM\s[A-Z]/i;
            const aIsTeam = teamRegex.test(a.doctorName);
            const bIsTeam = teamRegex.test(b.doctorName);

            if (aIsTeam && !bIsTeam) return -1; // TEAM comes first
            if (!aIsTeam && bIsTeam) return 1;  // TEAM comes first

            // If both are teams or both are not teams, sort alphabetically
            return a.doctorName.localeCompare(b.doctorName);
        }) : [];
        
        return (
            <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-orange-400 to-orange-500 p-6">
                {/* Header Section */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <div className="flex items-center space-x-4">
                            <img src="http://3.6.79.46:3000/logo.jpg" alt="Logo" className="h-40" />
                        </div>
                    </div>
                </div>

                {/* Dashboard Info Box */}
                <div className="bg-yellow-100 border-2 border-orange-300 rounded-lg p-6 mb-8 text-center">
                    <h2 className="text-brown-800 text-4xl font-bold mb-2">Previous Day Patient Admission Details (Doctor-wise) / पिछले दिन के रोगी भर्ती विवरण (डॉक्टर अनुसार )</h2>
                    <p className="text-brown-700 text-xl mb-1">The Dashboard Data will be refresh every 5 minutes / डैशबोर्ड डेटा हर 5 मिनट में रीफ्रेश होगा</p>
                    <p className="text-brown-700 text-xl">Data as on Date & Time : {lastUpdated || 'Loading...'} / दिनांक और समय के अनुसार डेटा</p>
                </div>

                {/* Doctor Admission Statistics */}
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Doctor Table */}
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        {loading ? (
                            <div className="text-center text-gray-600 text-xl">Loading doctor admission data...</div>
                        ) : (
                            <table className="border border-black text-center w-full text-lg shadow-lg">
                                <thead>
                                    <tr>
                                        <th className="bg-gray-300 border border-black px-4 py-2 font-bold">Doctor Name / डॉक्टर का नाम</th>
                                        <th className="bg-green-200 border border-black px-4 py-2 font-bold">Last Admission Count / अंतिम प्रवेश संख्या</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedDoctors.map((doctor, idx) => (
                                        <tr key={doctor.doctorName} className={idx % 2 === 0 ? "bg-yellow-100" : "bg-yellow-200"}>
                                            <td className="border border-black px-4 py-2 font-bold text-lg">{doctor.doctorName}</td>
                                            <td className="border border-black px-4 py-2 font-bold text-xl text-blue-800">{doctor.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Total Summary */}
                    {!loading && apiData && (
                        <div className="bg-blue-200 rounded-lg p-8 text-center shadow-lg">
                            <h3 className="text-brown-800 text-3xl font-bold mb-4">Total Last Admissions / कुल अंतिम प्रवेश</h3>
                            <p className="text-brown-800 text-6xl font-bold">
                                {sortedDoctors.reduce((total, doctor) => total + doctor.count, 0)}
                            </p>
                        </div>
                    )}
                </div>

                {/* Decorative Elements */}
                <div className="fixed bottom-0 right-0 opacity-20">
                    <div className="w-32 h-32 border-8 border-white transform rotate-45 translate-x-16 translate-y-16"></div>
                    <div className="w-24 h-24 border-6 border-white transform rotate-45 translate-x-20 translate-y-8"></div>
                    <div className="w-16 h-16 border-4 border-white transform rotate-45 translate-x-24 translate-y-4"></div>
                </div>
            </div>
            </>
        );
    };

    if (screen === 1) {
        return <Screen3 />;
    } else if (screen === 2) {
        return <Screen4 />;
    } else {
        return <Screen1 />;
    }
}

export default BedAvailability;